import React, { useCallback, useEffect, useState } from 'react';
import TierPageBanner from '../components/portfolio/TierPageBanner';
import { Link } from 'react-router-dom';
import { AlertTriangle, FlaskConical, Play, RefreshCw } from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  MacroDerivationMode,
  MacroDerivedRow,
  MacroFreshnessStatus,
  MacroIngestionRun,
  MacroObservation,
  MacroSeriesSummary,
  MacroTrack,
} from '../types/macroData';

const RAW_VIEW = 'raw';

interface ChartPoint {
  label: string;
  value: number;
  detail?: string;
}

type RangeOption = '5y' | '10y' | '25y' | 'max';

const RANGE_YEARS: Record<RangeOption, number | null> = {
  '5y': 5,
  '10y': 10,
  '25y': 25,
  max: null,
};

const formatValue = (value: number): string =>
  Math.abs(value) >= 1000
    ? value.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatCompact = (value: number): string =>
  Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);

const formatDateTime = (value?: string | null): string =>
  value ? new Date(value).toLocaleString() : '—';

const TRACKS: Array<{ track: MacroTrack; title: string; subtitle: string }> = [
  {
    track: 'real',
    title: 'Real economy',
    subtitle: 'Output, income, spending, investment and productivity',
  },
  {
    track: 'credit',
    title: 'Credit',
    subtitle: 'Bank lending, consumer credit, lending standards and debt service',
  },
  {
    track: 'inflation',
    title: 'Inflation',
    subtitle: 'Price indexes: the PCE deflator real-adjusts nominal series; all feed the regime overlay',
  },
  {
    track: 'expectations',
    title: 'Expectations',
    subtitle: 'Survey of Professional Forecasters: the baseline that growth and inflation surprises are measured against',
  },
  {
    track: 'rates',
    title: 'Interest rates',
    subtitle: 'Policy rate and Treasury yields: real rates and curve slope for the framework readings',
  },
  {
    track: 'labor',
    title: 'Labor',
    subtitle: 'Unemployment and wages: slack, and real wages against productivity',
  },
  {
    track: 'money',
    title: 'Money',
    subtitle: 'M2: money growth against output growth (quantity theory)',
  },
];

const FRESHNESS_STYLES: Record<MacroFreshnessStatus, string> = {
  current: 'bg-green-100 text-green-800',
  stale: 'bg-yellow-100 text-yellow-800',
  missing: 'bg-gray-100 text-gray-600',
};

const FreshnessBadge: React.FC<{ series: MacroSeriesSummary }> = ({ series }) => {
  const { status, daysSincePeriodEnd, staleAfterDays } = series.freshness;
  const title =
    daysSincePeriodEnd === null
      ? 'No observations stored'
      : `Latest period ended ${daysSincePeriodEnd} days ago (stale after ${staleAfterDays})`;
  return (
    <span
      title={title}
      className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${FRESHNESS_STYLES[status]}`}
    >
      {status}
    </span>
  );
};

const STATUS_STYLES: Record<MacroIngestionRun['status'], string> = {
  success: 'bg-green-100 text-green-800',
  partial: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
  running: 'bg-blue-100 text-blue-800',
};

const fromDateFor = (range: RangeOption): string | undefined => {
  const years = RANGE_YEARS[range];
  if (years === null) return undefined;
  const date = new Date();
  date.setUTCFullYear(date.getUTCFullYear() - years);
  return date.toISOString().slice(0, 10);
};

const VINTAGE_COLORS = ['#94a3b8', '#f59e0b', '#10b981', '#ef4444', '#2563eb'];

interface VintageOverlay {
  /** Vintage days drawn, oldest first; each is also a data key. */
  days: string[];
  data: Array<Record<string, number | string>>;
  revisedPeriods: number;
}

/**
 * Rebuilds the series as it stood at each vintage day: for every period, the
 * newest value stored on or before that day. Draws the first load plus the most
 * recent vintages so the chart stays readable.
 */
const buildVintageOverlay = (rows: MacroObservation[], maxLines = VINTAGE_COLORS.length): VintageOverlay => {
  const allDays = Array.from(new Set(rows.map((r) => r.vintageAt.slice(0, 10)))).sort();
  const days = allDays.length <= maxLines ? allDays : [allDays[0], ...allDays.slice(-(maxLines - 1))];
  const byPeriod = new Map<string, MacroObservation[]>();
  for (const row of rows) {
    const list = byPeriod.get(row.periodLabel) ?? [];
    list.push(row);
    byPeriod.set(row.periodLabel, list);
  }
  const data = Array.from(byPeriod, ([label, vintages]) => {
    const point: Record<string, number | string> = { label };
    for (const day of days) {
      const known = vintages.filter((v) => v.vintageAt.slice(0, 10) <= day);
      if (known.length) point[day] = known[known.length - 1].value;
    }
    return point;
  });
  const revisedPeriods = Array.from(byPeriod.values()).filter((v) =>
    v.some((row) => row.changeType === 'revision'),
  ).length;
  return { days, data, revisedPeriods };
};

const toChartPoints = (rows: MacroDerivedRow[], mode: MacroDerivationMode): ChartPoint[] =>
  rows.map((row) =>
    mode === 'point_in_time'
      ? { label: row.asOfLabel ?? row.periodLabel, value: row.value, detail: `latest period ${row.periodLabel}` }
      : { label: row.periodLabel, value: row.value },
  );

const SeriesCard: React.FC<{
  series: MacroSeriesSummary;
  observations: MacroObservation[];
  mode: MacroDerivationMode;
  from?: string;
}> = ({ series, observations, mode, from }) => {
  const locator = Object.values(series.locator).join(' / ');
  const [view, setView] = useState<string>(RAW_VIEW);
  const [derivedRows, setDerivedRows] = useState<MacroDerivedRow[]>([]);
  const [derivedLoading, setDerivedLoading] = useState(false);
  const [derivedError, setDerivedError] = useState<string | null>(null);
  const indicator = series.indicators.find((i) => i.indicatorId === view);
  const [showVintages, setShowVintages] = useState(false);
  const [vintageRows, setVintageRows] = useState<MacroObservation[] | null>(null);

  useEffect(() => {
    if (!showVintages) return;
    let cancelled = false;
    setVintageRows(null);
    apiService
      .getMacroObservations(series.seriesKey, { from, includeVintages: true })
      .then((response) => !cancelled && setVintageRows(response.observations))
      .catch(() => !cancelled && setVintageRows([]));
    return () => {
      cancelled = true;
    };
  }, [series.seriesKey, showVintages, from]);

  useEffect(() => {
    if (view === RAW_VIEW) return;
    let cancelled = false;
    setDerivedLoading(true);
    setDerivedError(null);
    apiService
      .getMacroDerived(series.seriesKey, { indicator: view, mode, from })
      .then((response) => !cancelled && setDerivedRows(response.rows))
      .catch((err: any) => !cancelled && setDerivedError(err?.response?.data?.message ?? err?.message ?? 'Failed to load'))
      .finally(() => !cancelled && setDerivedLoading(false));
    return () => {
      cancelled = true;
    };
  }, [series.seriesKey, view, mode, from]);

  const chartData: ChartPoint[] =
    view === RAW_VIEW
      ? observations.map((o) => ({ label: o.periodLabel, value: o.value }))
      : toChartPoints(derivedRows, mode);
  const overlay = view === RAW_VIEW && showVintages && vintageRows ? buildVintageOverlay(vintageRows) : null;
  const vintagesLoading = view === RAW_VIEW && showVintages && !vintageRows;
  const flagged = derivedRows.some((r) => r.flags.includes('partial_window') || r.flags.includes('stale'));
  const emptyMessage =
    view === RAW_VIEW
      ? 'No observations stored yet'
      : mode === 'point_in_time'
        ? 'No point-in-time history in this range yet. It builds up from the first month-end after data was first stored; earlier months exist only as latest revised.'
        : 'No derived values yet. They are computed after the next ingestion run.';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <CardTitle>{series.name}</CardTitle>
              <FreshnessBadge series={series} />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {series.source} · {locator} · {series.frequency} · {series.unit}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-semibold text-gray-900">
              {series.latest ? formatValue(series.latest.value) : '—'}
            </div>
            <div className="text-xs text-gray-500">
              {series.latest?.periodLabel ?? 'no data'}
              {series.latest?.status === 'preliminary' && ' (preliminary)'}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {series.lastError && (
          <div className="mb-3 flex items-start gap-2 rounded bg-red-50 p-2 text-xs text-red-700">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span className="break-all">{series.lastError}</span>
          </div>
        )}
        <div className="mb-2 flex items-center justify-between gap-2">
          <select
            value={view}
            onChange={(e) => setView(e.target.value)}
            className="max-w-full rounded border-gray-300 py-1 text-xs"
          >
            <option value={RAW_VIEW}>Raw observations ({series.unit})</option>
            {series.indicators.map((i) => (
              <option key={i.indicatorId} value={i.indicatorId}>
                {i.label}
              </option>
            ))}
          </select>
          {indicator && <span className="whitespace-nowrap text-xs text-gray-500">{indicator.unit}</span>}
          {view === RAW_VIEW && (
            <label className="flex items-center gap-1 whitespace-nowrap text-xs text-gray-600">
              <input
                type="checkbox"
                checked={showVintages}
                onChange={(e) => setShowVintages(e.target.checked)}
                className="rounded border-gray-300"
              />
              Overlay revisions
            </label>
          )}
        </div>
        {derivedError && view !== RAW_VIEW && (
          <div className="mb-2 rounded bg-red-50 p-2 text-xs text-red-700">{derivedError}</div>
        )}
        <div className="h-48">
          {(derivedLoading && view !== RAW_VIEW) || vintagesLoading ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">Loading…</div>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={overlay ? overlay.data : chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={40} />
                <YAxis
                  tick={{ fontSize: 10 }}
                  tickFormatter={formatCompact}
                  domain={['auto', 'auto']}
                  width={50}
                />
                <Tooltip
                  formatter={(v: number) => formatValue(v)}
                  labelFormatter={(label: string, payload: any[]) => {
                    const detail = payload?.[0]?.payload?.detail;
                    return detail ? `${label} (${detail})` : label;
                  }}
                />
                {view !== RAW_VIEW && indicator?.unit !== series.unit && (
                  <ReferenceLine y={0} stroke="#9ca3af" />
                )}
                {overlay ? (
                  overlay.days.map((day, i) => {
                    const latest = i === overlay.days.length - 1;
                    return (
                      <Line
                        key={day}
                        type="monotone"
                        dataKey={day}
                        name={`as of ${day}`}
                        stroke={latest ? VINTAGE_COLORS[VINTAGE_COLORS.length - 1] : VINTAGE_COLORS[i]}
                        strokeDasharray={latest ? undefined : '4 3'}
                        dot={false}
                        strokeWidth={latest ? 1.5 : 1}
                        isAnimationActive={false}
                      />
                    );
                  })
                ) : (
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2563eb"
                    dot={false}
                    strokeWidth={1.5}
                    isAnimationActive={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center px-4 text-center text-sm text-gray-400">
              {emptyMessage}
            </div>
          )}
        </div>
        {overlay && (
          <p className="mt-1 text-[11px] text-gray-400">
            {overlay.revisedPeriods
              ? `${overlay.revisedPeriods} period${overlay.revisedPeriods === 1 ? '' : 's'} revised in this range. Dashed lines show the series as stored on earlier days${overlay.days.length < 2 ? '' : ` (${overlay.days.join(', ')})`}.`
              : 'No revisions recorded in this range yet. Lines appear as agencies revise data after Marlin first stored it.'}
          </p>
        )}
        {view !== RAW_VIEW && chartData.length > 0 && (
          <p className="mt-1 text-[11px] text-gray-400">
            Unvalidated research indicator
            {mode === 'latest_revised' ? ' · today\'s revised values' : ' · as known at each month-end'}
            {flagged && ' · some points use a partial window or stale data'}
          </p>
        )}
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <dt className="text-gray-500">First seen by Marlin</dt>
          <dd className="text-gray-900">{formatDateTime(series.latest?.releaseTime)}</dd>
          <dt className="text-gray-500">Last ingested</dt>
          <dd className="text-gray-900">{formatDateTime(series.lastIngestedAt)}</dd>
          <dt className="text-gray-500">Periods stored</dt>
          <dd className="text-gray-900">{series.observationCount.toLocaleString()}</dd>
          <dt className="text-gray-500">Revisions recorded</dt>
          <dd className="text-gray-900">{series.revisionCount.toLocaleString()}</dd>
        </dl>
      </CardContent>
    </Card>
  );
};

const IngestionRunsPanel: React.FC<{ runs: MacroIngestionRun[] }> = ({ runs }) => (
  <Card>
    <CardHeader>
      <CardTitle>Ingestion runs</CardTitle>
    </CardHeader>
    <CardContent className="overflow-x-auto">
      {runs.length === 0 ? (
        <p className="text-sm text-gray-500">No ingestion runs yet.</p>
      ) : (
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-2 pr-4">Started</th>
              <th className="py-2 pr-4">Trigger</th>
              <th className="py-2 pr-4">Mode</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4">Fetched</th>
              <th className="py-2 pr-4">New</th>
              <th className="py-2 pr-4">Revised</th>
              <th className="py-2 pr-4">Unchanged</th>
              <th className="py-2 pr-4">Derived</th>
              <th className="py-2">Errors</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {runs.map((run) => {
              const errors = run.results.filter((r) => r.error);
              return (
                <tr key={run._id} className="align-top">
                  <td className="py-2 pr-4 whitespace-nowrap">{formatDateTime(run.startedAt)}</td>
                  <td className="py-2 pr-4">{run.trigger}</td>
                  <td className="py-2 pr-4">{run.mode}</td>
                  <td className="py-2 pr-4">
                    <span className={`rounded px-2 py-0.5 ${STATUS_STYLES[run.status]}`}>
                      {run.status}
                    </span>
                  </td>
                  <td className="py-2 pr-4">{run.totals?.fetched ?? '—'}</td>
                  <td className="py-2 pr-4">{run.totals?.inserted ?? '—'}</td>
                  <td className="py-2 pr-4">{run.totals?.revised ?? '—'}</td>
                  <td className="py-2 pr-4">{run.totals?.unchanged ?? '—'}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {run.derivation
                      ? `${run.derivation.written ?? 0} written · ${run.derivation.durationMs ?? 0} ms`
                      : '—'}
                  </td>
                  <td className="py-2 text-red-700">
                    {run.error && <div>{run.error}</div>}
                    {run.derivation?.error && <div>derivation: {run.derivation.error}</div>}
                    {errors.map((r) => (
                      <div key={r.seriesKey}>
                        <span className="font-medium">{r.seriesKey}:</span> {r.error}
                      </div>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </CardContent>
  </Card>
);

const MacroData: React.FC = () => {
  const [series, setSeries] = useState<MacroSeriesSummary[]>([]);
  const [observations, setObservations] = useState<Record<string, MacroObservation[]>>({});
  const [runs, setRuns] = useState<MacroIngestionRun[]>([]);
  const [range, setRange] = useState<RangeOption>('10y');
  const [mode, setMode] = useState<MacroDerivationMode>('latest_revised');
  const [loading, setLoading] = useState(true);
  const [ingesting, setIngesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [seriesList, health] = await Promise.all([
        apiService.getMacroSeries(),
        apiService.getMacroHealth(),
      ]);
      const from = fromDateFor(range);
      const responses = await Promise.all(
        seriesList.map((s) => apiService.getMacroObservations(s.seriesKey, { from })),
      );
      setSeries(seriesList);
      setRuns(health.runs);
      setObservations(
        Object.fromEntries(responses.map((r) => [r.series.seriesKey, r.observations])),
      );
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load macro data');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const runRefresh = async () => {
    setIngesting(true);
    try {
      await apiService.runMacroIngestion('refresh');
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Ingestion failed');
    } finally {
      setIngesting(false);
      load();
    }
  };

  return (
    <div className="space-y-6">
      <TierPageBanner
        title="Macro observations · Tier 1"
        description="Vintage macro series underpin Tier 1 IPS and sleeve target weights."
        primaryTier={1}
        thesisTimeframe="6_months_plus"
      />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Macro Data</h1>
          <p className="text-sm text-gray-500">
            Raw observations from BEA, BLS and the Federal Reserve, stored with vintages.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as MacroDerivationMode)}
            className="rounded border-gray-300 text-sm"
            title="Applies to derived indicators"
          >
            <option value="latest_revised">Latest revised</option>
            <option value="point_in_time">Point in time</option>
          </select>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value as RangeOption)}
            className="rounded border-gray-300 text-sm"
          >
            <option value="5y">5 years</option>
            <option value="10y">10 years</option>
            <option value="25y">25 years</option>
            <option value="max">Max</option>
          </select>
          <button
            onClick={load}
            className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" /> Reload
          </button>
          <Link
            to="/app/macro/cycle"
            className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Cycle view
          </Link>
          <button
            onClick={runRefresh}
            disabled={ingesting}
            className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Play className="h-4 w-4" /> {ingesting ? 'Ingesting…' : 'Run refresh'}
          </button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <FlaskConical className="h-5 w-5 flex-shrink-0" />
        <span>
          Research only. This data is isolated from the trading system and does not influence
          any decisions. History loaded on the first run reflects today's revised values, not
          what was known at the time. Derived indicators are unvalidated research defaults;
          "Point in time" shows only what Marlin had stored at each month-end.
        </span>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {TRACKS.map(({ track, title, subtitle }) => {
            const trackSeries = series.filter((s) => s.track === track);
            if (trackSeries.length === 0) return null;
            const counts = trackSeries.reduce<Record<MacroFreshnessStatus, number>>(
              (acc, s) => ({ ...acc, [s.freshness.status]: acc[s.freshness.status] + 1 }),
              { current: 0, stale: 0, missing: 0 },
            );
            return (
              <section key={track} className="space-y-3">
                <div className="flex flex-wrap items-end justify-between gap-2 border-b border-gray-200 pb-2">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                    <p className="text-xs text-gray-500">{subtitle}</p>
                  </div>
                  <div className="flex gap-2 text-xs">
                    {(Object.keys(counts) as MacroFreshnessStatus[]).map((status) => (
                      <span key={status} className={`rounded px-2 py-0.5 ${FRESHNESS_STYLES[status]}`}>
                        {counts[status]} {status}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
                  {trackSeries.map((s) => (
                    <SeriesCard
                      key={s.seriesKey}
                      series={s}
                      observations={observations[s.seriesKey] ?? []}
                      mode={mode}
                      from={fromDateFor(range)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
          <IngestionRunsPanel runs={runs} />
        </>
      )}
    </div>
  );
};

export default MacroData;
